// Per-user dashboard.
//
// Cards (issue #477 — the old shell only rendered "My open actions" after
// the OneDrive-dependent cards were removed with migration 6008):
//   - "My open actions"     -- prominent count + quick link to /actions
//   - "My overdue actions"  -- the overdue kind from the same badge-counts
//                              call (#960) — no second round trip
//   - "Recent meetings"     -- mcp.listMeetings, same pattern the Command
//                              Palette's "recent meetings" section already uses
//   - "Reports & dashboards" -- quick links to the new Management Overview
//                              and /reports surfaces
//
// None of these read the OneDrive file index. All data is read-only via
// the MCP.
//
// #960: both action cards used to read a `mcp.listActions({ limit: 100 })`
// fetch (actions.length / a client-side due_date filter over that same
// capped list), so a user with >100 open actions saw a wrong "My open
// actions" number and a potentially-wrong overdue count too. Both cards now
// read the exact counts off get_my_badge_counts() — one round trip, no cap.
//
// #1009 (Epic #956 phase 3b): the "My open actions" card now also links to
// /todo, the personal missing/stuck list that supersedes both of these
// cards as the single place to see everything that needs you (actions AND
// missing-item alerts), grouped by stage. The cards stay — they're still
// the quickest "how many / how overdue" glance — but the full itemised list
// lives at /todo now, not here.

import { useEffect, useState } from "react";
import { Link } from "react-router";
import {
  AlertCircle, AlertTriangle, BarChart3, ExternalLink, FileSpreadsheet,
  Inbox, Loader2, Users2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useMcp, mcp } from "@/lib/mcp";
import { supabase } from "@/lib/supabase";

function fmtDate(d: unknown): string {
  if (!d) return "—";
  const date = new Date(String(d));
  if (Number.isNaN(date.getTime())) return String(d);
  return date.toLocaleDateString("en-ZA", { month: "short", day: "numeric" });
}

export function DashboardPage() {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setUserId(data.user?.id ?? null));
  }, []);

  const { data: badgeCounts, error: badgeError, loading: badgeLoading } = useMcp(
    () => (userId ? mcp.getMyBadgeCounts() : Promise.resolve(null)),
    [userId],
  );
  const actionsKinds = badgeCounts?.surfaces?.["/actions"]?.kinds;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Dashboard</h2>
        <p className="text-sm text-muted-foreground">
          Today's snapshot: your open work.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MyOpenActionsCard count={actionsKinds?.open} error={badgeError} loading={badgeLoading} />
        <MyOverdueActionsCard count={actionsKinds?.overdue} error={badgeError} loading={badgeLoading} />
        <RecentMeetingsCard />
        <QuickLinksCard />
      </div>
    </div>
  );
}

function MyOpenActionsCard({
  count, error, loading,
}: {
  count: number | undefined;
  error: Error | null;
  loading: boolean;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          My open actions
        </CardTitle>
        <Inbox className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading actions…
          </div>
        )}
        {error && (
          <p className="text-xs text-destructive">{error.message}</p>
        )}
        {!loading && !error && (
          <>
            <p className="text-3xl font-bold tabular-nums">
              {count ?? 0}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              Items assigned to you, still open.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button asChild variant="outline" size="sm">
                <Link to="/actions">
                  Open inbox
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </Button>
              <Button asChild variant="ghost" size="sm">
                <Link to="/todo">
                  My to-do list
                  <ExternalLink className="h-3.5 w-3.5" />
                </Link>
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function MyOverdueActionsCard({
  count, error, loading,
}: {
  count: number | undefined;
  error: Error | null;
  loading: boolean;
}) {
  const overdueCount = count ?? 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          My overdue actions
        </CardTitle>
        <AlertTriangle className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && <p className="text-xs text-destructive">{error.message}</p>}
        {!loading && !error && (
          <>
            <p className={`text-3xl font-bold tabular-nums ${overdueCount > 0 ? "text-red-700" : ""}`}>
              {overdueCount}
            </p>
            <p className="mt-1 text-xs text-muted-foreground">
              {overdueCount > 0 ? "Past their due date." : "Nothing overdue — nice."}
            </p>
            <Button asChild variant="outline" size="sm" className="mt-3">
              <Link to="/actions">
                <AlertCircle className="h-3.5 w-3.5" />
                Review
              </Link>
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function RecentMeetingsCard() {
  const { data, error, loading } = useMcp(() => mcp.listMeetings({ limit: 5 }), []);
  const meetings = data?.meetings ?? [];

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Recent meetings
        </CardTitle>
        <Users2 className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {error && <p className="text-xs text-destructive">{error.message}</p>}
        {!loading && !error && meetings.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing logged yet.</p>
        )}
        {!loading && !error && meetings.length > 0 && (
          <ul className="space-y-1.5">
            {meetings.slice(0, 4).map((m) => (
              <li key={String(m.id)} className="flex items-center justify-between gap-2 text-xs">
                <Link to={`/meetings/${String(m.id)}`} className="truncate text-primary hover:underline">
                  {String(m.title ?? "(untitled)")}
                </Link>
                <span className="shrink-0 text-muted-foreground">{fmtDate(m.meeting_date)}</span>
              </li>
            ))}
          </ul>
        )}
        <Button asChild variant="outline" size="sm" className="mt-3">
          <Link to="/meetings">
            All meetings
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}

function QuickLinksCard() {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Reports & dashboards
        </CardTitle>
        <BarChart3 className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent className="space-y-2">
        <Button asChild variant="outline" size="sm" className="w-full justify-start">
          <Link to="/dashboard/management">
            <BarChart3 className="h-3.5 w-3.5" />
            Management overview
          </Link>
        </Button>
        <Button asChild variant="outline" size="sm" className="w-full justify-start">
          <Link to="/reports">
            <FileSpreadsheet className="h-3.5 w-3.5" />
            Generate a report
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
